import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWizardStore, OutlineItem } from './useWizardStore';
import WizardLayout from './WizardLayout';
import PageHeader from '../layout/PageHeader';
import api from '../../services/api';
import { toast } from '../../store/toastStore';
import {
  LightBulbIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { ensureUids, addRootItem, reorderDisplayIds } from '../../utils/outlineUtil';

const CHAPTER_TAGS = [
  '默认', '技术方案', '实施方案', '项目管理',
  '安全方案', '运维方案', '培训方案', '售后服务',
  '公司介绍', '团队介绍', '业绩案例', '需求分析',
];

function stripGenerating(nodes: any[]): any[] {
  return nodes.map((n: any) => {
    const { generating, ...rest } = n;
    return { ...rest, children: n.children ? stripGenerating(n.children) : n.children };
  });
}

const ModuleCheckItem: React.FC<{
  item: any;
  selected: Set<string>;
  onChange: (s: Set<string>) => void;
}> = ({ item, selected, onChange }) => {
  const checked = selected.has(item.id);
  const toggle = () => {
    const next = new Set(selected);
    if (checked) {
      next.delete(item.id);
      const deselectChildren = (children: any[]) => {
        children.forEach((c: any) => { next.delete(c.id); if (c.children) deselectChildren(c.children); });
      };
      if (item.children) deselectChildren(item.children);
    } else {
      next.add(item.id);
    }
    onChange(next);
  };

  return (
    <div>
      <label className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={toggle}
          className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
        />
        <span className="text-sm text-foreground">{item.id} {item.title}</span>
      </label>
      {item.children && (
        <div className="ml-6">
          {item.children.map((child: any) => (
            <ModuleCheckItem key={child.id} item={child} selected={selected} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
};

const StepUpload: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const project = useWizardStore((s) => s.project);
  const generatingOutline = useWizardStore((s) => s.generatingOutline);
  const outlineProgress = useWizardStore((s) => s.outlineProgress);
  const outlineProgressText = useWizardStore((s) => s.outlineProgressText);
  const setGeneratingOutline = useWizardStore((s) => s.setGeneratingOutline);
  const setOutlineProgress = useWizardStore((s) => s.setOutlineProgress);
  const setOutlineProgressText = useWizardStore((s) => s.setOutlineProgressText);
  const setCurrentStep = useWizardStore((s) => s.setCurrentStep);

  // 本地 outline state
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const [outlineLoading, setOutlineLoading] = useState(false);

  // mount 时从后端加载
  React.useEffect(() => {
    if (!id) return;
    setOutlineLoading(true);
    api.get(`/api/projects/${id}`)
      .then(res => {
        const raw = res.data.outline_data?.outline || [];
        setOutline(ensureUids(raw));
      })
      .catch(() => toast.error('加载目录失败'))
      .finally(() => setOutlineLoading(false));
  }, [id]);

  const [showOutlinePromptDialog, setShowOutlinePromptDialog] = useState(false);
  const [outlineCustomPrompt, setOutlineCustomPrompt] = useState('');
  const [showModuleSelect, setShowModuleSelect] = useState(false);
  const [moduleSelection, setModuleSelection] = useState<Set<string>>(new Set());

  const generateOutline = async (customPrompt?: string) => {
    if (!project) return;

    if (!project.model_config_id) {
      toast.warning('项目未配置模型，请先在项目设置中配置模型');
      return;
    }

    if (!project.project_overview || project.project_overview.trim() === '') {
      toast.warning('请先填写项目概述，这是生成目录的必要信息');
      return;
    }

    const isProposal = project.document_type === 'proposal';

    if (isProposal) {
      if (!project.business_requirements || project.business_requirements.trim() === '') {
        toast.warning('请先填写业务需求分析，这是生成方案目录的核心依据');
        return;
      }
    } else {
      if (!project.tech_requirements || project.tech_requirements.trim() === '') {
        toast.warning('请先填写技术评分项，这是生成目录的核心依据');
        return;
      }
    }

    setGeneratingOutline(true);
    setOutlineProgress(0);
    setOutlineProgressText('正在准备生成目录...');

    try {
      let requestBody: any = {
        overview: project.project_overview || '',
        requirements: isProposal ? (project.business_requirements || '') : (project.tech_requirements || ''),
        uploaded_expand: false,
        document_type: project.document_type || 'bid',
        proposal_mode: (project as any).proposal_mode || 'normal',
        expected_word_count: (project as any).expected_word_count || 0,
      };

      if (customPrompt && customPrompt.trim()) {
        requestBody.requirements = `${isProposal ? (project.business_requirements || '') : (project.tech_requirements || '')}\n\n【用户补充要求】\n${customPrompt}`;
        if (outline.length > 0) {
          requestBody.uploaded_expand = true;
          requestBody.old_outline = JSON.stringify({ outline });
        }
      }

      setOutlineProgress(10);
      setOutlineProgressText('正在连接 AI 服务...');

      let response;
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
        const token = localStorage.getItem('token');
        response = await fetch(`${API_BASE_URL}/api/projects/${id}/generate/outline`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(requestBody),
        });
      } catch (fetchError: any) {
        console.error('网络请求失败:', fetchError);
        throw new Error(`无法连接到后端服务。请确保：\n1. 后端服务已启动\n2. 后端地址正确（http://localhost:8000）\n\n错误详情: ${fetchError.message}`);
      }

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) { /* ignore */ }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.detail) {
            errorMessage = errorJson.detail;
          }
        } catch (e) {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(`生成目录失败: ${errorMessage}`);
      }

      setOutlineProgress(20);
      setOutlineProgressText('正在生成目录结构...');

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('无法读取响应流，请检查浏览器兼容性');
      }

      const decoder = new TextDecoder();
      let fullText = '';
      let lastProgressUpdate = Date.now();

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6).trim();

            if (data === '[DONE]') continue;
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);

              if (parsed.error) {
                throw new Error(parsed.message || '服务器返回错误');
              }

              if (parsed.type === 'generation_progress') {
                setOutlineProgress(parsed.percentage);
                setOutlineProgressText(parsed.message);
                continue;
              }

              if (parsed.chunk !== undefined) {
                fullText += parsed.chunk;

                const now = Date.now();
                if (now - lastProgressUpdate > 500) {
                  const estimatedProgress = Math.min(80, 20 + Math.floor(fullText.length / 50));
                  setOutlineProgress(estimatedProgress);
                  setOutlineProgressText(`正在生成目录... (已接收 ${fullText.length} 字符)`);
                  lastProgressUpdate = now;
                }
              }
            } catch (e) {
              console.warn('解析SSE数据失败:', e);
            }
          }
        }
      }

      setOutlineProgress(85);
      setOutlineProgressText('正在解析目录数据...');

      if (!fullText || fullText.trim() === '') {
        throw new Error('未收到目录数据');
      }

      let cleanedResult = fullText.trim();
      if (cleanedResult.startsWith('```json')) {
        cleanedResult = cleanedResult.replace(/^```json\s*/, '').replace(/\s*```$/, '');
      } else if (cleanedResult.startsWith('```')) {
        cleanedResult = cleanedResult.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      // 增强 JSON 清理：提取第一个 { 到最后一个 } 之间的内容
      const firstBrace = cleanedResult.indexOf('{');
      const lastBrace = cleanedResult.lastIndexOf('}');
      if (firstBrace >= 0 && lastBrace > firstBrace) {
        cleanedResult = cleanedResult.substring(firstBrace, lastBrace + 1);
      }

      // 移除行内注释（// 开头的注释，但不能误删 URL 中的 //）
      cleanedResult = cleanedResult.replace(/(^|[^:])\/\/.*$/gm, '$1');
      // 移除多余的尾逗号（},] 或 },} 前的逗号）
      cleanedResult = cleanedResult.replace(/,(\s*[}\]])/g, '$1');
      // 修复中文引号
      cleanedResult = cleanedResult.replace(/[""]/g, '"').replace(/['']/g, "'");

      let result;
      try {
        result = JSON.parse(cleanedResult);
      } catch (parseError) {
        console.error('JSON解析失败:', parseError);
        throw new Error(`目录数据格式错误: ${parseError instanceof Error ? parseError.message : '未知错误'}`);
      }

      if (!result) throw new Error('解析结果为空');

      if (result.answer && !result.outline) {
        throw new Error(`AI 无法生成目录：${result.answer}`);
      }

      if (Array.isArray(result)) {
        result = { outline: result };
      }

      if (!result.outline) {
        const possibleKeys = ['chapters', 'items', 'data', 'content', 'structure'];
        for (const key of possibleKeys) {
          if (result[key] && Array.isArray(result[key])) {
            result.outline = result[key];
            break;
          }
        }
      }

      if (!result.outline || !Array.isArray(result.outline)) {
        throw new Error(`目录数据格式不正确。\n\n收到的数据: ${JSON.stringify(result).substring(0, 200)}...`);
      }

      if (result.outline.length === 0) {
        throw new Error('生成的目录为空');
      }

      setOutlineProgress(90);
      setOutlineProgressText('正在保存目录...');

      const ensured = ensureUids(result.outline);
      setOutline(ensured);

      await api.put(`/api/projects/${id}`, {
        outline_data: { outline: ensured },
      });

      setOutlineProgress(100);
      setOutlineProgressText('目录生成完成！');

      // Show module selection popup
      const allIds = new Set<string>();
      const collectIds = (items: any[]) => {
        items.forEach((item: any) => {
          allIds.add(item.id);
          if (item.children) collectIds(item.children);
        });
      };
      collectIds(ensured);
      setModuleSelection(allIds);
      setShowModuleSelect(true);

      setTimeout(() => {
        toast.success('请跳转到目录生成步骤检查并编辑目录。', '目录生成成功');
        setOutlineProgress(0);
        setOutlineProgressText('');
      }, 500);
    } catch (error: any) {
      console.error('生成目录失败:', error);
      toast.error(`生成目录失败: ${error.message}`);
      setOutlineProgress(0);
      setOutlineProgressText('');
    } finally {
      setGeneratingOutline(false);
    }
  };

  const handleRegenerateOutline = () => {
    setOutlineCustomPrompt('');
    setShowOutlinePromptDialog(true);
  };

  const handleConfirmRegenerateOutline = () => {
    setShowOutlinePromptDialog(false);
    generateOutline(outlineCustomPrompt);
  };

  const isProposal = project?.document_type === 'proposal';
  const hasIncompleteInfo = !project?.project_overview ||
    (isProposal ? !project?.business_requirements : !project?.tech_requirements);

  return (
    <div>
      <PageHeader
        title="上传招标文件"
        subtitle="确认项目信息，AI 将根据项目需求自动生成标书/方案目录"
      />
      <WizardLayout>
        {/* 生成目录按钮区域 */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">
              {isProposal ? '第一步：生成方案目录' : '第一步：生成标书目录'}
            </h2>
            <p className="text-foreground mb-4">
              {isProposal
                ? <>根据需求文档的<strong>业务需求分析</strong>和项目概述，AI 将自动生成方案的目录结构。</>
                : <>根据招标文件的<strong>技术评分项</strong>和项目概述，AI 将自动生成标书的目录结构。</>
              }
            </p>
            <div className="mb-6 p-3 bg-primary/10 border border-primary/20 rounded-xl">
              <p className="text-sm text-primary">
                <LightBulbIcon className="w-5 h-5 inline-block" />{' '}
                <strong>提示：</strong>
                {isProposal
                  ? '目录将基于业务需求域生成，确保每个需求域都有对应的解决方案章节。生成后您可以编辑、添加或删除章节。'
                  : '目录将基于技术文件评分项生成，确保每个评分项都有对应的章节。生成后您可以编辑、添加或删除章节，也可以使用自定义提示词重新生成。'
                }
              </p>
            </div>

            {/* 项目信息不完整警告 */}
            {hasIncompleteInfo && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  <ExclamationTriangleIcon className="w-5 h-5 inline-block" /> 项目信息不完整
                </p>
                <ul className="text-sm text-yellow-700 space-y-1 ml-4 list-disc">
                  {!project?.project_overview && <li>缺少项目概述</li>}
                  {isProposal
                    ? !project?.business_requirements && <li>缺少业务需求分析</li>
                    : !project?.tech_requirements && <li>缺少技术评分项</li>
                  }
                </ul>
                <p className="text-sm text-yellow-700 mt-2">
                  请先返回项目列表，编辑项目补充完整信息后再生成目录。
                </p>
              </div>
            )}

            {/* 进度条 */}
            {generatingOutline && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-primary">{outlineProgressText}</span>
                  <span className="text-sm font-medium text-primary">{outlineProgress}%</span>
                </div>
                <div className="w-full bg-accent rounded-full h-2.5">
                  <div
                    className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${outlineProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Tablet 风格按钮组 — 目录生成操作 */}
            <div className="inline-flex items-center gap-0 rounded-lg border border-border bg-card overflow-hidden shadow-sm">
              <button
                onClick={() => generateOutline()}
                disabled={generatingOutline}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors border-r border-primary-foreground/20 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
                {generatingOutline ? '正在生成目录...' : outline.length > 0 ? '重新生成目录' : '开始生成目录'}
              </button>
              {outline.length > 0 && (
                <button
                  onClick={handleRegenerateOutline}
                  disabled={generatingOutline}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
                  自定义重新生成
                </button>
              )}
            </div>
          </div>

          {/* 目录编辑 */}
          {outline.length > 0 && (
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-foreground">目录结构</h3>
                <button onClick={() => {
                  const { items: newItems } = addRootItem(outline);
                  setOutline(newItems);
                }} className="btn-secondary text-xs !px-3 !py-1.5">
                  + 添加章节
                </button>
              </div>
              <div className="border border-border rounded-xl p-4 max-h-[50vh] overflow-y-auto">
                {outline.map((item: any) => (
                  <div key={item.id || item.uid}>
                    <div className="flex items-center gap-2 py-1.5 group hover:bg-background rounded px-2">
                      <span className="text-sm font-medium text-primary">{item.id}</span>
                      <span className="text-sm text-foreground flex-1">{item.title}</span>
                    </div>
                    {item.children && item.children.map((child: any) => (
                      <div key={child.id || child.uid} className="ml-6 flex items-center gap-2 py-1 text-sm text-foreground">
                        <span className="text-primary">{child.id}</span>
                        <span>{child.title}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-primary/10 border border-brand-100 rounded-xl">
                <p className="text-sm text-primary">
                  确认无误后点击下方按钮，AI 将自动为每个章节撰写内容。
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 导航按钮 */}
        <div className="flex justify-between mt-6">
          <div />
          <button
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors shadow-sm disabled:opacity-50"
            onClick={async () => {
              if (outline.length === 0) { toast.warning('请先生成目录'); return; }
              try {
                await api.put(`/api/projects/${id}`, { outline_data: { outline } });
                navigate(`/projects/${id}/word`);
              } catch { toast.error('保存目录失败'); }
            }}
            disabled={outline.length === 0}
          >
            确认目录，开始撰写
          </button>
        </div>
      </WizardLayout>

      {/* 自定义提示词对话框 - 目录生成 */}
      {showOutlinePromptDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-border p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">重新生成目录 - 自定义要求</h3>
            <p className="text-sm text-foreground mb-2">
              输入您对目录结构的特殊要求或补充说明。AI 将在原有{isProposal ? '业务需求分析' : '技术评分项'}的基础上，结合您的要求重新生成目录。
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              留空则按原{isProposal ? '业务需求分析' : '技术评分项'}重新生成。
            </p>
            <textarea
              value={outlineCustomPrompt}
              onChange={(e) => setOutlineCustomPrompt(e.target.value)}
              placeholder="例如：请增加项目管理相关章节，减少技术细节章节..."
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowOutlinePromptDialog(false);
                  setOutlineCustomPrompt('');
                }}
                className="px-4 py-2 text-foreground bg-accent rounded-xl hover:bg-accent"
              >
                取消
              </button>
              <button
                onClick={handleConfirmRegenerateOutline}
                className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              >
                确认重新生成
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Module selection popup */}
      {showModuleSelect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-[480px] max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-base font-bold text-foreground">选择生成模块</h3>
                <p className="text-xs text-muted-foreground mt-0.5">勾选需要生成内容的章节，未勾选的将跳过</p>
              </div>
              <button onClick={() => setShowModuleSelect(false)} className="p-1 hover:bg-accent rounded">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1">
              <div className="flex gap-2 mb-3">
                <button onClick={() => {
                  const all = new Set<string>();
                  const walk = (items: any[]) => items.forEach((i: any) => { all.add(i.id); if (i.children) walk(i.children); });
                  walk(outline);
                  setModuleSelection(all);
                }} className="text-xs text-primary hover:text-primary">全选</button>
                <span className="text-muted-foreground">|</span>
                <button onClick={() => setModuleSelection(new Set())} className="text-xs text-muted-foreground hover:text-foreground">取消全选</button>
              </div>

              {outline.map((item: any) => (
                <ModuleCheckItem key={item.id} item={item} selected={moduleSelection} onChange={setModuleSelection} />
              ))}
            </div>

            <div className="px-6 py-4 border-t flex gap-3">
              <button
                onClick={() => {
                  const filterOutline = (items: any[]): any[] => {
                    return items
                      .filter((item: any) => moduleSelection.has(item.id))
                      .map((item: any) => ({
                        ...item,
                        children: item.children ? filterOutline(item.children) : undefined,
                      }));
                  };
                  const filtered = filterOutline(outline);
                  if (filtered.length === 0) {
                    toast.warning('请至少选择一个章节');
                    return;
                  }
                  setOutline(reorderDisplayIds(filtered));
                  setShowModuleSelect(false);
                }}
                className="flex-1 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl"
              >
                确认选择（{moduleSelection.size} 章）
              </button>
              <button
                onClick={() => setShowModuleSelect(false)}
                className="px-6 py-2.5 text-sm text-muted-foreground hover:text-foreground"
              >
                跳过（保留全部）
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepUpload;
